/**
 * @file ui/src/i18n.ts
 * @description i18next 初始化
 * @copyright 2026 Seth Zhai. BSL 1.1
 *
 * DESIGN RATIONALE:
 *   - 语言优先级：JWT locale → localStorage → 浏览器语言 → 'zh'
 *   - Phase 0 只支持 zh / en，其余 locale 回退到 zh
 *   - locale 文件用静态 import，避免动态 import 在 CJS 输出中的问题（ADR-108）
 *   - users.locale 字段已在 ADR-135 中加入，JWT 签发时注入
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import zh from './locales/zh.json'
import en from './locales/en.json'

// ─── 从 JWT payload 读取 locale（无需完整验签，仅读展示字段）────────────────

function readLocaleFromJwt(): string | null {
  try {
    const token = localStorage.getItem('jarvis_token')
    if (!token) return null
    const payload = token.split('.')[1]
    if (!payload) return null
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return decoded.locale ?? null
  } catch {
    return null
  }
}

// ─── 语言检测顺序 ─────────────────────────────────────────────────────────────

function detectLanguage(): string {
  // 1. JWT locale（后端 users.locale 字段）
  const jwtLocale = readLocaleFromJwt()
  if (jwtLocale === 'zh' || jwtLocale === 'en') return jwtLocale

  // 2. localStorage 手动设置（设置页切换后写入）
  const stored = localStorage.getItem('spark_locale')
  if (stored === 'zh' || stored === 'en') return stored

  // 3. 浏览器语言（zh-CN → zh，en-US → en）
  const browser = navigator.language.slice(0, 2).toLowerCase()
  if (browser === 'en') return 'en'

  // 4. 默认中文
  return 'zh'
}

// ─── 初始化 ───────────────────────────────────────────────────────────────────

i18n
  .use(initReactI18next)
  .init({
    resources: {
      zh: { translation: zh },
      en: { translation: en },
    },
    lng:           detectLanguage(),
    fallbackLng:   'zh',
    interpolation: {
      escapeValue: false,  // React 已处理 XSS
    },
    // 缺失 key 时返回 key 本身，便于 Phase 1 发现未翻译字符串
    saveMissing:   false,
    missingKeyHandler: (lngs, _ns, key) => {
      if (import.meta.env.DEV) {
        console.warn(`[i18n] missing key: ${key} (${lngs.join(',')})`)
      }
    },
  })

export default i18n

// ─── 语言切换工具函数（设置页调用）──────────────────────────────────────────

export function setLocale(locale: 'zh' | 'en'): void {
  i18n.changeLanguage(locale)
  localStorage.setItem('spark_locale', locale)
}

export type SupportedLocale = 'zh' | 'en'
export const SUPPORTED_LOCALES: SupportedLocale[] = ['zh', 'en']
